import { useEffect, useRef, useState } from "react";
import type React from "react";
import { useForm, Controller } from "react-hook-form";
import styles from "./UtilAdminUtilityForm.module.scss";
import TextInput from "../../../../../common/components/xepp/text-input/TextInput";
import Button from "../../../../../common/components/xepp/button/Button";
import { UtilityIcon } from "../../../../../assets/icons";
import { useGetCountriesQuery } from "../../../../../service/utility-service";
import type { Utility } from "../../../../../model/bill-settings/bill-settings";
import { hasPermission } from "../../../../../guards/common/authentification-functions";
import { UtilityPermissions } from "../../../../../model/permissions/permissions";

interface Country {
	id: number;
	isDeleted: boolean;
	name: string;
	imageUrl: string;
	isoCode: string;
}

interface UtilAdminUtilityFormProps {
	utility: Utility;
	/**
	 * Function to handle saving a new utility
	 */
	onSave: (data: any) => void;
	isSaving: boolean;
}

export enum TariffTypes {
	postpaid = "Postpaid",
	prepaid = "Prepaid",
	both = "Both",
}

interface FormValues {
	id: string;
	name: string;
	imageFile: File | null;
	imageUrl: string | null;
	countryId: number;
	utilityCode: string;
	chargeType: "fixed" | "percentage";
	percentageCharge: string;
	fixedCharge: string;
	tariff: TariffTypes;
}

const UtilAdminUtilityForm: React.FC<UtilAdminUtilityFormProps> = ({ utility, onSave, isSaving }) => {
	const { data: countries = [], isLoading: countriesLoading } = useGetCountriesQuery();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [hasChanges, setHasChanges] = useState(false);
	const canEdit = hasPermission(UtilityPermissions.EditUtilitySettings);

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			id: utility?.id || "",
			name: utility?.name || "",
			imageFile: null,
			imageUrl: utility?.imageUrl || null,
		},
	});

	const watchName = watch("name");
	const watchImageFile = watch("imageFile");

	useEffect(() => {
		if (utility) {
			reset({
				id: utility?.id || "",
				name: utility.name || "",
				imageFile: null,
				imageUrl: utility.imageUrl || null,
			});
			setHasChanges(false);
		}
	}, [utility, reset]);

	// Track changes
	useEffect(() => {
		const nameChanged = watchName !== utility?.name;
		const imageChanged = watchImageFile !== null;
		
		setHasChanges(nameChanged || imageChanged);
	}, [watchName, watchImageFile, utility?.name]);

	const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;
		if (file) {
			setValue("imageFile", file);
		}
	};

	const handleCancel = () => {
		reset({
			id: utility?.id || "",
			name: utility?.name || "",
			imageFile: null,
			imageUrl: utility?.imageUrl || null,
		});
		setHasChanges(false);
		
		// Clear file input
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const getInitials = () => {
		if (!watchName) return "CA";
		const words = watchName.split(" ");
		if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
		return (words[0][0] + words[1][0]).toUpperCase();
	};

	const selectedCountry = countries.find((country: Country) => country.id === utility?.countryId);

	const getDisplayPictureUrl = () => {
		if (watchImageFile) {
			return URL.createObjectURL(watchImageFile);
		}
		return utility?.imageUrl || null;
	};

	const onSubmit = async (data: FormValues) => {
		let imageToSubmit = data.imageFile;

		const submitData = {
			...data,
			imageFile: imageToSubmit,
		};

		onSave(submitData);
	};

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div className={styles.iconContainer}>
					<UtilityIcon />
				</div>
				<h2 className={styles.title}>
					{canEdit ? "Edit Utility" : "Utility"}
				</h2>
			</div>

			<form onSubmit={handleSubmit(onSubmit)} className={styles.bodyContainer}>
				<div className={canEdit ? styles.formContainer : styles.formContainerViewOnly}>
					<div className={styles.displayPictureSection}>
						<span className={styles.sectionLabel}>Add Display Picture</span>
						<div className={styles.displayPictureContainer}>
							<input
								type="file"
								id="imageFile"
								ref={fileInputRef}
								accept="image/jpeg,image/png,image/gif"
								onChange={handleImageFileChange}
								className={styles.fileInput}
								disabled={!canEdit}
							/>
							<label htmlFor="imageFile" className={canEdit ? styles.displayPictureLabel : styles.displayPictureLabelViewOnly}>
								{getDisplayPictureUrl() ? (
									<img src={getDisplayPictureUrl() ?? "/placeholder.svg"} alt="Display" className={styles.displayPictureImage} />
								) : (
									<div className={styles.displayPicturePlaceholder}>{getInitials()}</div>
								)}
							</label>
						</div>
					</div>

					<Controller
						name="name"
						control={control}
						rules={{ required: "Utility name is required" }}
						render={({ field, fieldState }) => (
							<TextInput
								label="Utility Name"
								value={field.value}
								onChange={field.onChange}
								placeholder="Enter utility name"
								error={fieldState.error?.message ?? ""}
								disabled={!canEdit}
							/>
						)}
					/>

					<div className={styles.formDivider}></div>

					<div className={`${styles.formSection} ${styles.row}`}>
						<span className={styles.sectionLabel}>Tariff</span>
						<div className={`${styles.radioGroup} ${styles.row}`}>
							<label className={styles.radioLabel}>
								<input disabled type="radio" checked={utility?.tariffPlan?.tariff === "Postpaid"} readOnly className={styles.radioInput} />
								<span className={styles.radioText}>Postpaid</span>
							</label>
							<label className={styles.radioLabel}>
								<input disabled type="radio" checked={utility?.tariffPlan?.tariff === "Prepaid"} readOnly className={styles.radioInput} />
								<span className={styles.radioText}>Prepaid</span>
							</label>
							<label className={styles.radioLabel}>
								<input disabled type="radio" checked={utility?.tariffPlan?.tariff === "Both"} readOnly className={styles.radioInput} />
								<span className={styles.radioText}>Both</span>
							</label>
						</div>
					</div>

					<div className={styles.formDivider}></div>

					<div className={`${styles.formSection} ${styles.row}`}>
						<span className={styles.sectionLabel}>Country</span>
						{countriesLoading ? (
							<div className={styles.loading}>Loading countries...</div>
						) : (
							<>
								{selectedCountry && (
									<div className={styles.countryGrid}>
										<div className={styles.countryItem}>
											<div className={styles.countryInfo}>
												<img alt={`${selectedCountry.name} flag`} className={styles.countryFlag} src={selectedCountry.imageUrl || "/placeholder.svg"} />
												<span className={styles.countryName}>
													{selectedCountry.name} ({selectedCountry.isoCode})
												</span>
											</div>
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				{canEdit && <div className={styles.actions}>
					{hasChanges && (
						<Button disabled={isSaving} text="Cancel" variant="outline" type="button" onClick={handleCancel} />
					)}
					{canEdit && 
						<Button disabled={isSaving} text={isSaving ? "Editing..." : "Edit Setup"} variant="secondary" type="submit" />}
				</div>}
			</form>
		</div>
	);
};

export default UtilAdminUtilityForm;